import {
  CourtAvailabilityTemplateDto,
} from '../../../models/court/dto/court-availability-template.dto';
import {
  CreateCourtFormAvailabilityInputType,
  isMisencodedTemplateRule,
  transformRuleToInput,
} from '../../../models/court/dto/court-availability-template.constants';

export type AvailabilityFormSeed = {
  isGameAvailable: boolean;
  isCoachingAvailable: boolean;
  returnAsOneChunk: boolean;
  availabilities: CreateCourtFormAvailabilityInputType[];
  /**
   * At least one seeded block was written by the pre-fix slot encoder, so the
   * hours shown are not the hours that were configured. Drives a warning; it
   * never blocks the save, because correcting the sliders and saving IS the
   * remedy.
   */
  hasMisencodedRules: boolean;
};

/**
 * Everything the availabilities form restores from a court's saved template.
 *
 * This exists as one function, rather than four `useState` initializers, for
 * the reason the fourth one was missing: `returnAsOneChunk` was hardcoded to
 * `false` while `isGameAvailable`, `isCoachingAvailable` and the rules were
 * all seeded from the template. The column's default is `true` (both in the
 * entity and in `Migration20250611074446`), so every template written before
 * that migration carries `true` — and the modal sent `false` back on every
 * save, as the expansion directive AND onto the persisted template. That is a
 * one-way ratchet: `expandCompactAvailabilities` emits one availability per
 * config per slot when it is off, so an 08:00–18:00 block carrying two
 * capacities becomes one availability per hour per capacity, and
 * `court-booking-base-fees` — which matches `startTime <= bookingStart AND
 * endTime >= bookingEnd` — then finds no row for any booking that does not sit
 * exactly on one slot. The operator changed a price and the slots 404.
 *
 * Seeding is all-or-nothing on purpose: the caller re-runs this whenever the
 * `court` prop changes, and restoring the rules while leaving the three flags
 * at whatever the operator had touched is how a half-reset form gets saved.
 */
export function seedAvailabilityFormState(
  court: { availabilityTemplates?: CourtAvailabilityTemplateDto[] } | undefined,
  makeDefaultAvailability: () => CreateCourtFormAvailabilityInputType,
): AvailabilityFormSeed {
  const template = court?.availabilityTemplates?.[0];

  if (!template) {
    return {
      isGameAvailable: true,
      isCoachingAvailable: false,
      // NOT the entity default (`true`). The entity default only ever applied
      // to rows backfilled by Migration20250611074446; every dashboard write
      // sends the flag explicitly. A court with no saved template has no
      // configured shape to preserve, so this stays what the form has always
      // offered — flipping it would change how a brand-new schedule is
      // materialised, which is a product call, not part of this fix.
      returnAsOneChunk: false,
      availabilities: [makeDefaultAvailability()],
      hasMisencodedRules: false,
    };
  }

  return {
    isGameAvailable: template.isGameAvailable,
    isCoachingAvailable: template.isCoachingAvailable,
    returnAsOneChunk: template.returnAsOneChunk,
    availabilities: template.rules.map((rule) => transformRuleToInput(rule)),
    hasMisencodedRules: template.rules.some((rule) =>
      isMisencodedTemplateRule(rule),
    ),
  };
}
